import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Copy, Check } from "lucide-react";
import { api } from "@/lib/api";
import { resolveMediaUrl, uploadMedia } from "@/lib/media";

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const load = () => api.get("/admin/media").then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const handleFiles = async (files) => {
    setError(""); setBusy(true);
    try {
      for (const f of files) await uploadMedia(f);
      await load();
    } catch (e) {
      setError(e.message || "Error al subir");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar imagen?")) return;
    await api.delete(`/admin/media/${id}`);
    load();
  };

  const copy = (url) => {
    navigator.clipboard.writeText(resolveMediaUrl(url));
    setCopied(url); setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div data-testid="admin-media">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-1">Medios</h1>
          <p className="text-sm text-muted-foreground">Galería de imágenes reutilizables. Total: {items.length}</p>
        </div>
        <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="upload-btn" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-sm font-semibold text-sm disabled:opacity-50">
          <Upload className="w-4 h-4" /> {busy ? "Subiendo..." : "Subir imágenes"}
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(Array.from(e.target.files || []))} />
      </div>

      {error && <p className="text-sm text-destructive mb-4">{error}</p>}

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(Array.from(e.dataTransfer.files || [])); }}
        className="border-2 border-dashed border-border rounded-sm p-8 text-center text-muted-foreground mb-8 hover:border-primary transition-colors cursor-pointer"
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="w-8 h-8 mx-auto mb-3" />
        <p className="text-sm">Arrastrá imágenes aquí o click para seleccionar</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {items.map((it) => (
          <div key={it.id} className="border border-border rounded-sm overflow-hidden group" data-testid={`media-${it.id}`}>
            <div className="aspect-square bg-secondary relative">
              <img src={resolveMediaUrl(it.url)} alt={it.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button onClick={() => copy(it.url)} className="px-3 py-1.5 bg-background text-foreground text-xs font-semibold rounded-sm inline-flex items-center gap-1">
                  {copied === it.url ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied === it.url ? "Copiado" : "Copiar URL"}
                </button>
                <button onClick={() => del(it.id)} className="px-3 py-1.5 bg-destructive text-destructive-foreground text-xs font-semibold rounded-sm inline-flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> Eliminar
                </button>
              </div>
            </div>
            <div className="p-2">
              <p className="text-xs truncate font-semibold">{it.name}</p>
              <p className="text-[10px] text-muted-foreground">{Math.round((it.size || 0) / 1024)} KB</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
